// CatererGrid.js
import * as React from "react";
import {useCallback, useState} from "react";
import GridElement from "../../Common/GridElement";
import {makeAccessor, makeHeader, prepareFilterQuery, prepareSortQuery} from "../../Utils/gridUtils";
import {Add, Archive, Edit, Unarchive} from "@mui/icons-material";
import Button from "@mui/material/Button";
import ManageCatererModal from "./ManageCatererModal";
import {FormControlLabel, IconButton, Switch, Tooltip} from "@mui/material";
import Swal from "sweetalert2";

import {archiveCaterer, getCaterers, restoreCaterer} from "../../../API/caterer";
import {toast} from "react-toastify";

const CatererGrid = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [rowsCount, setRowsCount] = useState(0);
    const [filters, setFilters] = useState([]);
    const [controlledPageIndex, setControlledPageIndex] = useState(0);
    const [refreshGridData, setRefreshGridData] = useState(false);
    const [openManageCatererModal, setOpenManageCatererModal] = useState(false);
    const [caterer, setCaterer] = useState(null);
    const [viewArchived, setViewArchived] = useState(false);
    const viewArchivedRef = React.useRef(viewArchived);

    // Update the ref whenever viewArchived changes
    React.useEffect(() => {
        viewArchivedRef.current = viewArchived;
    }, [viewArchived]);

    const [cols, _setCols] = useState([
        {
            column: "company_name",
            accessor: makeAccessor("company_name"),
            minWidth: 150,
            Header: makeHeader("Company Name"),
            filter: "fuzzyText",
        },
        {
            column: "contact_person_name",
            accessor: makeAccessor("contact_person_name"),
            minWidth: 150,
            Header: makeHeader("Contact Person"),
            filter: "fuzzyText",
        },
        {
            column: "country",
            accessor: makeAccessor("country"),
            minWidth: 150,
            Header: makeHeader("Country"),
            filter: "fuzzyText",
        },
        {
            column: "delivery_address",
            accessor: makeAccessor("delivery_address"),
            minWidth: 200,
            Header: makeHeader("Delivery Address"),
            filter: "fuzzyText",
        },
        {
            column: "region",
            accessor: makeAccessor("region"),
            minWidth: 150,
            Header: makeHeader("Region/City"),
            filter: "fuzzyText",
        },
        {
            column: "email",
            accessor: makeAccessor("email"),
            minWidth: 200,
            Header: makeHeader("Email"),
            filter: "fuzzyText",
        },
        {
            column: "phone",
            accessor: makeAccessor("phone"),
            minWidth: 150,
            Header: makeHeader("Phone"),
            filter: "fuzzyText",
        },
        {
            Header: "Action",
            Cell: ({row}) => (
                <div>
                    {!viewArchived && (
                        <>
                            <Tooltip title='Edit' arrow placement='top'>
                                <IconButton
                                    onClick={() => {
                                        setOpenManageCatererModal(true);
                                        setCaterer(row.original);
                                    }}
                                >
                                    <Edit color='primary'/>
                                </IconButton>
                            </Tooltip>

                            {!row.original.is_archive && (<Tooltip title='Archive Caterer' arrow placement='top'>
                                    <IconButton
                                        onClick={() => handleArchiveCatererClick(row.original)}
                                    >
                                        <Archive sx={{color: "orange"}}/>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    )}

                    {Boolean(row.original.is_archive) && (
                        <Tooltip title='Restore Caterer' arrow placement='top'>
                            <IconButton onClick={() => handleRestoreCatererClick(row.original)}>
                                <Unarchive sx={{color: "green"}}/>
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            ),
        }
    ]);

    const resetFilters = useCallback(() => setFilters([]), []);

    const fetchData = React.useCallback(
        ({pageSize, pageIndex, sortBy, filters, columns}) => {
            const _columns = columns.map(item => item.column).filter(Boolean);
            const query = prepareFilterQuery(filters, columns);
            const sort = prepareSortQuery(sortBy, columns);
            const viewArchived = viewArchivedRef.current;
            setLoading(true);

            getCaterers(pageSize, _columns, pageIndex, query, sort, viewArchived)
                .then((res) => {
                    setData(res.data.main);
                    setPageCount(res.data.offset.highest_offset);
                    setRowsCount(res.data.offset.rows_count);
                })
                .catch((error) => toast.error(error.error))
                .finally(() => setLoading(false));

            setRefreshGridData(false);
        },
        [viewArchived] // Add viewArchived as a dependency
    );

    React.useEffect(() => {
        setControlledPageIndex(0);
        setRefreshGridData(true);
    }, [viewArchived]);

    const handleManageCatererClose = (refreshGrid = false) => {
        setOpenManageCatererModal(false);
        setRefreshGridData(refreshGrid);
        setCaterer(null);
    };

    const handleArchiveCatererClick = (caterer) => {
        Swal.fire({
            title: `Archive ${caterer.company_name}?`,
            text: "Archived caterers will not appear in active lists but can be restored later.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Confirm Archive",
        }).then(result => {
            if (result.isConfirmed) {
                archiveCaterer(caterer.caterer_id).then(() => {
                    toast.success("Caterer archived.");
                    setRefreshGridData(true);
                }).catch(err => toast.error(err));
            }
        });
    };

    const handleRestoreCatererClick = (caterer) => {
        restoreCaterer(caterer.caterer_id).then(() => {
            toast.success("Caterer restored.");
            setRefreshGridData(true);
        }).catch(err => toast.error(err));
    };

    return (
        <>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <h2 className='page-title'>Caterer Management</h2>
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => setOpenManageCatererModal(true)}
                    disabled={viewArchived}
                >
                    <Add/> Add New Caterer
                </Button>
            </div>

            <div style={{margin: "10px 0"}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={viewArchived}
                            onChange={(e) => {
                                setViewArchived(e.target.checked);
                                // Removed the manual refresh here - now handled by useEffect
                            }}
                        />
                    }
                    label="Show Archived Caterers"
                />
            </div>

            <GridElement
                columns={cols}
                data={data}
                fetchData={fetchData}
                loading={loading}
                pageCount={pageCount}
                controlledPageIndex={controlledPageIndex}
                setControlledPageIndex={setControlledPageIndex}
                filters={filters}
                setFilters={setFilters}
                resetFilters={resetFilters}
                rowsCount={rowsCount}
                handleRowClick={() => {
                }}
                initialSortBy='created_at'
                sortOrder='desc'
                hideSelection={true}
                refreshGridData={refreshGridData}
                manual // Ensure the grid is in manual mode
            />

            <ManageCatererModal
                open={openManageCatererModal}
                handleClose={handleManageCatererClose}
                data={caterer}
            />
        </>
    );
};

export default CatererGrid;